/**
 * «Βρες την οθόνη» — οδηγός για τεχνικό χωρίς ειδικές γνώσεις. Ο server δεν μπορεί να «δει» την οθόνη πελάτη·
 * τη βλέπει ο τεχνικός (ή ο υπάλληλος στο τηλέφωνο). Γι' αυτό:
 *   1. η οθόνη δείχνει αριθμούς, ένας ανά συνδυασμό θύρας/ταχύτητας/τύπου, και ο τεχνικός πατά αυτόν που
 *      διαβάζεται ΚΑΘΑΡΑ (ό,τι κάνει το vfd-probe.js, χωρίς γραμμή εντολών)·
 *   2. η οθόνη δείχνει 5 φορές το «ΚΑΛΗΜΕΡΑ ΣΑΣ» και ο τεχνικός πατά αυτό που φαίνεται σωστά·
 *   3. αποθήκευση — ο agent ανοίγει την οθόνη αμέσως, χωρίς επανεκκίνηση, και δείχνει «ΚΑΛΩΣ ΗΡΘΑΤΕ».
 * Όσο κρατά ο οδηγός, η οθόνη και το modem του ταμείου είναι σε παύση (ο agent επιστρέφει μόνος του σε 3′).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Monitor } from 'lucide-react';
import { GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN } from '../shared/listPage';
import { DevicesApi, supportDevicesApi } from './cashierDevicesApi';
import {
  Combo, GREEK_TRIALS, Pc, comboLines, displayCombos, encodingLabel, greekLines, isPortUnavailable, mergeSave, modeLabel,
} from './cashierDevicesModel';

type Step = 'intro' | 'scan' | 'greek' | 'save' | 'verify';

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const errorOf = (e: any) => e?.response?.data?.error || 'Αποτυχία επικοινωνίας';
const slug = (s: string | null) => (s || 'tameio').replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'tameio';

const NUM_BTN = 'w-14 h-14 rounded-xl border-2 border-line-strong bg-surface-2 text-xl font-bold text-t1 hover:border-brand hover:bg-brand/10 '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand';

const CashierDisplayWizard: React.FC<{
  storeId: number;
  pc: Pc;
  onDone: () => void;
  onCancel: () => void;
  holdMs?: number;
  api?: DevicesApi;
}> = ({ storeId, pc, onDone, onCancel, holdMs = 2000, api = supportDevicesApi }) => {
  const uid = pc.uid as string;
  const live = pc.live!;
  const [step, setStep] = useState<Step>('intro');
  const [slow, setSlow] = useState(false);
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState<Combo | null>(null);
  const [total, setTotal] = useState(0);
  const [shown, setShown] = useState<Combo[]>([]);
  const [skipped, setSkipped] = useState<Array<{ port: string; error: string }>>([]);
  const [chosen, setChosen] = useState<Combo | null>(null);
  const [greekShown, setGreekShown] = useState<number[]>([]);
  const [greek, setGreek] = useState<(typeof GREEK_TRIALS)[number] | null>(null);
  const [name, setName] = useState(live.display.config?.name || `Ταμείο ${pc.machine || ''}`.trim());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const stop = useRef(false);
  const finished = useRef(false);

  // Κλείσιμο στη μέση (π.χ. Esc στο παράθυρο): η οθόνη και το modem γυρίζουν αμέσως σε λειτουργία.
  useEffect(() => () => {
    stop.current = true;
    if (!finished.current) api.deviceCmd(storeId, uid, 'resume').catch(() => {});
  }, [storeId, uid, api]);

  const wait = async (ms: number) => {
    for (let t = 0; t < ms && !stop.current; t += 100) await sleep(100);
  };

  const show = (args: Record<string, unknown>) =>
    api.deviceCmd(storeId, uid, 'display-show', args).catch((e: any) => ({ ok: false, error: errorOf(e) })) as Promise<{ ok: boolean; error?: string }>;

  const runScan = async () => {
    const combos = displayCombos(live.ports);
    stop.current = false;
    setError(null); setShown([]); setSkipped([]); setChosen(null); setTotal(combos.length);
    setStep('scan'); setRunning(true);
    const dead = new Set<string>();
    for (const c of combos) {
      if (stop.current) break;
      if (dead.has(c.port)) continue;
      setCurrent(c);
      const r = await show({ port: c.port, baudRate: c.baudRate, mode: c.mode, lines: comboLines(c) });
      if (stop.current) break;
      if (!r.ok) {
        if (isPortUnavailable(r.error || '')) { dead.add(c.port); setSkipped(s => [...s, { port: c.port, error: r.error || '' }]); }
        continue;
      }
      setShown(s => [...s, c]);
      await wait(slow ? holdMs * 2 : holdMs);
    }
    setCurrent(null);
    setRunning(false);
  };

  const pick = (c: Combo) => {
    stop.current = true;
    setChosen(c);
    setGreekShown([]);
    setGreek(null);
    setRunning(false);
    setStep('greek');
  };

  const runGreek = async () => {
    if (!chosen) return;
    stop.current = false;
    setError(null); setGreekShown([]); setGreek(null); setRunning(true);
    for (const t of GREEK_TRIALS) {
      if (stop.current) break;
      const r = await show({ port: chosen.port, baudRate: chosen.baudRate, mode: chosen.mode, encoding: t.encoding, codePage: t.codePage, lines: greekLines(t.n) });
      if (stop.current) break;
      if (!r.ok) { setError(r.error || 'Αποτυχία'); break; }
      setGreekShown(s => [...s, t.n]);
      await wait(Math.round(holdMs * 1.5));
    }
    setRunning(false);
  };

  const pickGreek = (n: number) => {
    stop.current = true;
    setGreek(GREEK_TRIALS.find(t => t.n === n) || null);
    setRunning(false);
    setStep('save');
  };

  const save = async () => {
    if (!chosen || !greek) return;
    setSaving(true); setError(null);
    const display = {
      enabled: true,
      port: chosen.port,
      baudRate: chosen.baudRate,
      mode: chosen.mode,
      encoding: greek.encoding,
      codePage: greek.codePage,
      // Ίδιο αναγνωριστικό με πριν, αν υπήρχε: τα ταμεία θυμούνται ποια οθόνη έχουν διαλέξει.
      id: live.display.displayId || live.display.config?.id || slug(pc.machine),
      name: name.trim() || null,
    };
    try {
      await api.saveDevices(storeId, uid, mergeSave(pc, { display }));
      finished.current = true;
      setStep('verify');
    } catch (e: any) {
      setError(errorOf(e));
    } finally { setSaving(false); }
  };

  const cancel = () => { stop.current = true; onCancel(); };

  return (
    <div className="rounded-xl border-2 border-brand/40 bg-surface-1 p-4 space-y-4" data-testid="display-wizard">
      <p className="flex items-center gap-2 font-semibold text-t1"><Monitor className="w-4 h-4" aria-hidden="true" /> Βρες την οθόνη πελάτη</p>

      {step === 'intro' && (
        <>
          <ol className="list-decimal pl-5 space-y-1.5 text-sm text-t2">
            <li>Η οθόνη πελάτη πρέπει να είναι <b>αναμμένη</b> και συνδεδεμένη στον υπολογιστή <b>{pc.machine}</b>.</li>
            <li>Κοίτα την οθόνη (ή ζήτα από τον υπάλληλο να την κοιτά): θα δείχνει <b>αριθμούς</b>, έναν κάθε {slow ? holdMs * 2 / 1000 : holdMs / 1000} δευτερόλεπτα.</li>
            <li>Μόλις φανεί ένας αριθμός <b>καθαρά</b>, πάτησέ τον εδώ.</li>
          </ol>
          <p className="text-xs text-t3">Όσο γίνεται η ρύθμιση, η οθόνη και η αναγνώριση κλήσης αυτού του ταμείου είναι σε παύση.</p>
          <label className="flex items-center gap-2 text-sm text-t2">
            <input type="checkbox" checked={slow} onChange={e => setSlow(e.target.checked)} />
            Πιο αργά (για όταν ο υπάλληλος λέει τον αριθμό στο τηλέφωνο)
          </label>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} className={GHOST_BTN}>Άκυρο</button>
            <button type="button" onClick={runScan} className={PRIMARY_BTN} data-testid="wizard-start">Ξεκίνα</button>
          </div>
        </>
      )}

      {step === 'scan' && (
        <>
          <div aria-live="polite" className="text-sm text-t2">
            {running && current ? (
              <div className="flex items-center gap-4">
                <span className="text-4xl font-bold text-t1 tabular-nums w-14 text-center">{current.n}</span>
                <span>
                  <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Η οθόνη δείχνει τώρα αυτόν τον αριθμό.</span>
                  <span className="block text-xs text-t3">Δοκιμή {current.n} από {total} · μόλις δεις αριθμό καθαρά, πάτησέ τον παρακάτω</span>
                </span>
              </div>
            ) : <p>Τέλος. Πάτησε τον αριθμό που φάνηκε <b>καθαρά</b> στην οθόνη.</p>}
          </div>
          {shown.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="wizard-numbers">
              {shown.map(c => (
                <button key={c.n} type="button" onClick={() => pick(c)} className={NUM_BTN} aria-label={`Είδα τον αριθμό ${c.n}`}>{c.n}</button>
              ))}
            </div>
          )}
          {skipped.length > 0 && (
            <p className="text-xs text-t3">Προσπεράστηκαν: {skipped.map(s => `${s.port} (${s.error})`).join(', ')}</p>
          )}
          {!running && (
            <Notice>
              <span>Δεν φάνηκε κανένας αριθμός; Έλεγξε ότι η οθόνη είναι αναμμένη και το καλώδιό της μπαίνει καλά στον υπολογιστή, και ξαναδοκίμασε. Αν δεις «σκουπίδια» αντί για αριθμό, δεν είναι αυτός.</span>
            </Notice>
          )}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} className={GHOST_BTN}>Άκυρο</button>
            {running
              ? <button type="button" onClick={() => { stop.current = true; }} className={GHOST_BTN}>Σταμάτα</button>
              : <button type="button" onClick={runScan} className={GHOST_BTN} data-testid="wizard-again">Ξανά από την αρχή</button>}
          </div>
        </>
      )}

      {step === 'greek' && chosen && (
        <>
          <p className="text-sm text-t2">
            Βρέθηκε: <b>{chosen.port}</b> · {chosen.baudRate} · {modeLabel(chosen.mode)}. Τώρα τα ελληνικά: η οθόνη θα δείξει 5 φορές τη φράση
            <b> «ΚΑΛΗΜΕΡΑ ΣΑΣ»</b> με έναν αριθμό μπροστά. Πάτησε τον αριθμό που τη δείχνει <b>σωστά</b>.
          </p>
          {greekShown.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="wizard-greek">
              {greekShown.map(n => (
                <button key={n} type="button" onClick={() => pickGreek(n)} className={NUM_BTN} aria-label={`Σωστά ελληνικά στο ${n}`}>{n}</button>
              ))}
            </div>
          )}
          <p className="text-xs text-t3">Αν καμία δεν δείχνει σωστά ελληνικά, διάλεξε το <b>5</b>: η οθόνη θα γράφει με λατινικά.</p>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} className={GHOST_BTN}>Άκυρο</button>
            <button type="button" onClick={() => { stop.current = true; setStep('scan'); }} className={GHOST_BTN}>Πίσω</button>
            <button type="button" onClick={runGreek} disabled={running} className={PRIMARY_BTN} data-testid="wizard-greek-start">
              {running ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
              {greekShown.length ? 'Δείξε τα ξανά' : 'Δείξε τα ελληνικά'}
            </button>
          </div>
        </>
      )}

      {step === 'save' && chosen && greek && (
        <>
          <ul className="text-sm text-t2 space-y-1">
            <li>Θύρα: <b>{chosen.port}</b> · ταχύτητα <b>{chosen.baudRate}</b> · τύπος <b>{modeLabel(chosen.mode)}</b></li>
            <li>Ελληνικά: <b>{encodingLabel(greek.encoding, greek.codePage)}</b></li>
          </ul>
          <label className="block text-sm text-t2">
            Όνομα οθόνης (το βλέπει ο ταμίας όταν τη διαλέγει)
            <input value={name} onChange={e => setName(e.target.value)} maxLength={80} className={`${INPUT_CLS} mt-1`} data-testid="wizard-name" />
          </label>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} className={GHOST_BTN}>Άκυρο</button>
            <button type="button" onClick={save} disabled={saving} className={PRIMARY_BTN} data-testid="wizard-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null} Αποθήκευση
            </button>
          </div>
        </>
      )}

      {step === 'verify' && (
        <>
          <p className="text-sm text-t2">Αποθηκεύτηκε. Σε λίγα δευτερόλεπτα η οθόνη πρέπει να δείχνει <b>«XENTRIX FLOW»</b> και <b>«ΚΑΛΩΣ ΗΡΘΑΤΕ»</b>. Το βλέπεις;</p>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { finished.current = false; setStep('intro'); }} className={GHOST_BTN}>Όχι, ξανά από την αρχή</button>
            <button type="button" onClick={onDone} className={PRIMARY_BTN} data-testid="wizard-done">Ναι, φαίνεται σωστά</button>
          </div>
        </>
      )}

      {error && <Notice tone="danger" testId="wizard-error">{error}</Notice>}
    </div>
  );
};

export default CashierDisplayWizard;
